import {
  useCallback,
  useRef,
  type KeyboardEvent,
  type MutableRefObject,
} from "react";

/**
 * Minimal description of a tab the hook needs to navigate. Real callers
 * carry richer per-tab data (labels, test ids, etc.) but the hook only
 * cares about identity (`id`) and whether the tab is interactive.
 */
export type TablistKeyboardItem<T extends string> = {
  id: T;
  disabled?: boolean;
};

export type UseTablistKeyboardNavigationArgs<T extends string> = {
  items: ReadonlyArray<TablistKeyboardItem<T>>;
  /** Currently selected tab id. */
  value: T;
  /** Called when the user navigates to a different (enabled) tab. */
  onChange: (next: T) => void;
};

export type UseTablistKeyboardNavigationResult = {
  /**
   * Mutable ref array — assign each tab DOM node by index:
   *   `ref={(node) => { tabRefs.current[i] = node; }}`
   *
   * The hook reads from this array to move focus when arrow / Home / End
   * keys fire; it never writes to it.
   */
  tabRefs: MutableRefObject<Array<HTMLElement | null>>;
  /**
   * Attach to every tab via
   *   `onKeyDown={(event) => handleKeyDown(event, index)}`.
   *
   * Implements the WAI-ARIA Authoring Practices "tablist" pattern with
   * automatic activation: ArrowLeft / ArrowRight move (and select) the
   * previous / next enabled tab, wrapping at the ends; Home / End jump
   * to the first / last enabled tab; disabled tabs are skipped; other
   * keys pass through untouched.
   */
  handleKeyDown: (
    event: KeyboardEvent<HTMLElement>,
    currentIndex: number,
  ) => void;
};

/**
 * Shared keyboard-navigation logic for any horizontal ARIA tablist.
 *
 * Used by:
 *   - `shared/TabSelector` (brand-underline tab strip used in the Game
 *     Pass → Create Season → Challenges step).
 *   - `GamePass/SeasonsList` filter strip (segmented pill group above
 *     the active / scheduled timeline).
 *
 * Both consumers own their own JSX, ref attachment, and visual styles.
 * The hook only contributes the `onKeyDown` behaviour the ARIA tablist
 * pattern requires (without which keyboard-only users cannot move
 * between tabs at all).
 */
export function useTablistKeyboardNavigation<T extends string>({
  items,
  value,
  onChange,
}: UseTablistKeyboardNavigationArgs<T>): UseTablistKeyboardNavigationResult {
  const tabRefs = useRef<Array<HTMLElement | null>>([]);

  /**
   * Walk the items array starting at `from` and return the index of the
   * next enabled tab in `direction` (+1 forward / -1 backward), wrapping
   * around. Returns `-1` only if every tab is disabled — in that case
   * the caller leaves focus untouched.
   */
  const findNextEnabledIndex = useCallback(
    (from: number, direction: 1 | -1): number => {
      if (items.length === 0) return -1;
      let next = from;
      for (let step = 0; step < items.length; step += 1) {
        next = (next + direction + items.length) % items.length;
        if (!items[next]?.disabled) return next;
      }
      return -1;
    },
    [items],
  );

  const findFirstEnabledIndex = useCallback(
    (direction: 1 | -1): number => {
      const start = direction === 1 ? -1 : items.length;
      return findNextEnabledIndex(start, direction);
    },
    [findNextEnabledIndex, items.length],
  );

  const activateIndex = useCallback(
    (index: number) => {
      const target = items[index];
      if (!target || target.disabled) return;
      tabRefs.current[index]?.focus();
      if (target.id !== value) onChange(target.id);
    },
    [items, onChange, value],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>, currentIndex: number) => {
      switch (event.key) {
        case "ArrowRight": {
          const next = findNextEnabledIndex(currentIndex, 1);
          if (next === -1 || next === currentIndex) return;
          event.preventDefault();
          activateIndex(next);
          return;
        }
        case "ArrowLeft": {
          const next = findNextEnabledIndex(currentIndex, -1);
          if (next === -1 || next === currentIndex) return;
          event.preventDefault();
          activateIndex(next);
          return;
        }
        case "Home": {
          const next = findFirstEnabledIndex(1);
          if (next === -1 || next === currentIndex) return;
          event.preventDefault();
          activateIndex(next);
          return;
        }
        case "End": {
          const next = findFirstEnabledIndex(-1);
          if (next === -1 || next === currentIndex) return;
          event.preventDefault();
          activateIndex(next);
          return;
        }
        default:
      }
    },
    [activateIndex, findFirstEnabledIndex, findNextEnabledIndex],
  );

  return { tabRefs, handleKeyDown };
}
